/**
 * Turn a server failure into something a student can act on.
 *
 * A 422 from the shared zod schemas carries `details` as an array of issues
 * (`{ path, message }`). The messages are English and internal, so we never
 * show them: we map `path[0]` onto the field that owns it and write our own
 * sentence. Unknown codes get a plain-language fallback, never the raw string.
 */
import { ApiError } from "./api";

export interface ZodIssueLike {
  path: Array<string | number>;
  message?: string;
  code?: string;
}

/** Field labels used in the generated messages. */
const FIELD_LABELS: Record<string, string> = {
  invite_code: "班级邀请码",
  token: "邀请码",
  username: "用户名",
  password: "密码",
  new_password: "新密码",
  current_password: "当前密码",
  display_name: "姓名",
  dialect_l1: "方言背景",
  hometown: "家乡",
  training_level: "训练情况",
  birth_year: "出生年份",
  gender: "性别",
  contact: "联系方式",
  train: "训练授权",
  display: "展示授权",
  commercial: "商业授权",
  minor_guardian_signed: "监护人联签",
};

/** Field-specific wording; anything else falls back to a generic sentence. */
const FIELD_MESSAGES: Record<string, string> = {
  invite_code: "这个班级邀请码用不了，请找老师确认。",
  username: "用户名只能用 3–32 位小写字母、数字或下划线。",
  password: "密码不符合要求：至少 10 位，且不要包含用户名。",
  new_password: "新密码不符合要求：至少 10 位，且不要包含用户名。",
  display_name: "请填写你的姓名。",
  dialect_l1: "方言背景请从列表里选，至少选一个。",
  training_level: "请选一个训练情况。",
  birth_year: "请选择出生年份。",
  gender: "性别这一项的取值不对，重新选一次。",
  contact: "联系方式填得不对，检查一下。",
};

function issuesOf(err: unknown): ZodIssueLike[] {
  if (!(err instanceof ApiError)) return [];
  const d = err.details;
  const raw = Array.isArray(d)
    ? d
    : d && typeof d === "object" && Array.isArray((d as { issues?: unknown }).issues)
      ? ((d as { issues: unknown[] }).issues as unknown[])
      : [];
  return raw.filter(
    (i): i is ZodIssueLike =>
      Boolean(i) && typeof i === "object" && Array.isArray((i as ZodIssueLike).path),
  );
}

/**
 * Field name → message, for a form to render inline.
 * Empty when the error is not a validation failure.
 */
export function fieldErrors(err: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of issuesOf(err)) {
    const key = issue.path.find((p) => typeof p === "string") as string | undefined;
    if (!key || out[key]) continue;
    out[key] = FIELD_MESSAGES[key] ?? `${FIELD_LABELS[key] ?? "这一项"}填得不对，请检查一下。`;
  }
  return out;
}

/** Fields named by a 422, in order — used to jump back to the right step. */
export function invalidFields(err: unknown): string[] {
  return Object.keys(fieldErrors(err));
}

const STATUS_MESSAGES: Record<number, string> = {
  0: "网络连接不可用，检查一下网络再试。",
  400: "提交的内容有几项不对，按提示改一下。",
  401: "登录状态过期了，请重新登录。",
  403: "你现在没有权限做这件事。",
  404: "没找到对应的内容，可能已经被撤回了。",
  409: "这条和当前状态冲突了，刷新一下再试。",
  413: "文件太大，传不上去。",
  422: "提交的内容有几项不对，按提示改一下。",
  429: "尝试次数过多，请 15 分钟后再试。",
  500: "服务出了点问题，稍后再试。",
};

/**
 * One sentence for the top of a form. `known` lets a page translate the codes
 * it understands (INVITE_EXPIRED, BAD_CREDENTIALS…); everything else gets a
 * status-based sentence rather than the server's own wording.
 */
export function friendlyMessage(
  err: unknown,
  fallback = "操作没有成功，请稍后重试。",
  known: Record<string, string> = {},
): string {
  if (!(err instanceof ApiError)) return fallback;
  if (known[err.code]) return known[err.code]!;
  const fields = fieldErrors(err);
  const first = Object.values(fields)[0];
  if (first) return first;
  return STATUS_MESSAGES[err.status] ?? fallback;
}
